/**
 * What a trade does to the picks a league actually holds, and to the keepers standing on them.
 *
 * TWO THINGS THE APP HAD NO RUNTIME ANSWER FOR, 1 September, eight days out:
 *
 *   1. WHO OWNS A PICK AFTER A TRADE FILED IN THE APP. `ownership2026` is emitted at build time
 *      from `data/pick-ownership-2026.json`, so a trade settled through the Trades page changed
 *      the ledger and nothing else — the draftboard, the keeper resolver and the trade form itself
 *      went on believing the seller still held the pick until somebody edited a data file and
 *      deployed. `pickTransfersFrom` reads the settled live trades so the board can be the
 *      baseline plus everything that has happened since.
 *
 *   2. WHAT HAPPENS TO A KEEPER WHOSE PICK LEAVES. 2015-III-7 charges him to a pick his GM owns at
 *      or before his cost round; sell that pick and he has to be charged to another one, always
 *      earlier, never later. The mock trade tool worked this out in a `useMemo` on the draft board
 *      and the live trade form did not work it out at all. `keeperEffect` is that calculation as a
 *      pure function, so both can use it and a GM filing a real trade sees the same warning a
 *      mock would have shown him.
 *
 * Tim, 1 September: keeper-occupied picks stay SELECTABLE, with a warning; and the app needs "a
 * mechanism to then reallocate any disrupted keepers, including ones that would need to be
 * shifted up by escalation". The reallocation is `fitKeepers` with each keeper's current round as
 * his ceiling — he may be pushed earlier by the trade, he is never quietly slid later by it — and
 * `moves` is the list of who moved and where, in the words the card and the activity log print.
 */

import type { KeeperDeclaration, LiveTradeDoc } from '../store/types.js';
import type { PickTrade, PickRef } from './board.js';
import { pickCoords } from './tradeWindow.js';
import { assignKeeperPicks } from './draftOrder.js';
import { feasibility, fitKeepers, type Shortfall, type SlotKeeper } from './keeperSlots.js';

/* ------------------------------------------------------------ picks that have changed hands */

/**
 * Every pick that moved in a SETTLED trade this season, as the swap the board applies.
 *
 * Settled only. An affirmed trade inside its window is still a proposal the league can veto and
 * the parties can counter out of; the ledger row is written when the window closes, and that is
 * when the pick is somebody else's. `withdrawn` never settles, so it needs no separate test.
 *
 * The seat a pick comes off is `origin` where the asset carries one and the sender's own seat
 * where it does not — the same default `overallOf` uses, and the reason the origin selector only
 * ever appears where a man holds two in a round.
 */
export function pickTransfersFrom(
  trades: readonly LiveTradeDoc[], season: number,
): PickTrade[] {
  const out: PickTrade[] = [];
  const settled = trades
    .filter((t) => t.settledAs && !t.withdrawn && t.season === season)
    .sort((x, y) => (x.agreedAt ?? x.filedAt).localeCompare(y.agreedAt ?? y.filedAt));
  for (const t of settled) {
    const refs = (side: 0 | 1): PickRef[] => {
      const s = t.sides[side];
      const picks: PickRef[] = [];
      for (const a of s.sends) {
        if (a.kind !== 'pick') continue;
        if (!a.text.includes(String(season))) continue;
        const c = pickCoords(a.text);
        if (!c) continue;
        picks.push({ round: c.round, seat: (a.origin ?? s.gm).toUpperCase() });
      }
      return picks;
    };
    out.push({
      a: t.sides[0].gm.toUpperCase(),
      b: t.sides[1].gm.toUpperCase(),
      aGets: refs(1),
      bGets: refs(0),
    });
  }
  return out;
}

/* ------------------------------------------------------------------- what it does to keepers */

export interface KeeperMove {
  player: string;
  /** Null for a keeper who arrived in the trade: he has no round here to move from. */
  from: number | null;
  to: number;
}

export interface KeeperEffect {
  gm: string;
  /** Where the slate runs out of picks, if it does. When set, `moves` is empty and the trade is refused. */
  shortfall: Shortfall | null;
  /** Who has to be charged to an earlier pick, and where. Empty when nobody is disturbed. */
  moves: KeeperMove[];
  /** player -> the round he is charged to after the trade, for every keeper, moved or not. */
  placed: Map<string, number>;
}

/**
 * One GM's keepers, before and after a trade that changes which rounds he holds.
 *
 * `now` is where each keeper is charged today, which is two things at once: the round the card
 * says he moves FROM, and the CEILING on where the re-fit may put him. A keeper standing on a
 * 5th today is never placed on a 6th by a trade, even where the trade brings a 6th in and the
 * Grid would allow it — that would be the app quietly repricing a submitted slate downward in a
 * screen that is about something else. He can take the cheaper round himself, on Declare.
 *
 * `gives` and `gets` are rounds, not picks: the resolver works in rounds and the equal-picks
 * rule means a GM can hold a round twice, so both are multisets and are applied as such.
 */
export function keeperEffect(
  gm: string,
  keepers: readonly SlotKeeper[],
  now: ReadonlyMap<string, number>,
  ownedRounds: readonly number[],
  gives: readonly number[],
  gets: readonly number[],
  /**
   * THE GM'S OWN CHOICE of who takes the hit, player -> round. 2015-III-7 forces some
   * resolution and leaves the choice to him; the fit below is only what the resolver would do
   * on its own. A pin outside a keeper's legal range is refused and he is placed like anybody
   * else, so a stale choice cannot strand him.
   */
  pinned: Readonly<Record<string, number>> = {},
): KeeperEffect {
  const after = [...ownedRounds];
  for (const r of gives) {
    const i = after.indexOf(r);
    if (i >= 0) after.splice(i, 1);
  }
  after.push(...gets);

  const who = gm.toUpperCase();
  if (keepers.length === 0) return { gm: who, shortfall: null, moves: [], placed: new Map() };

  const shortfall = feasibility(keepers, after);
  if (shortfall) return { gm: who, shortfall, moves: [], placed: new Map() };

  const ceiling: Record<string, number> = {};
  for (const [player, round] of now) ceiling[player] = round;
  /* A pin is the GM's word; it overrides his ceiling for that man, and only that man. */
  for (const [player, round] of Object.entries(pinned)) {
    if (keepers.some((k) => k.player === player)) ceiling[player] = Math.max(ceiling[player] ?? 0, round);
  }
  const fit = fitKeepers(keepers, after, pinned, ceiling);
  /*
   * A ceiling can strand a keeper that a free fit would have placed: pin everybody at or before
   * where he sits and there may be no arrangement left. `feasibility` said the slate fits SOME
   * way, so fall back — first dropping the ceilings, then the pins — rather than refuse a trade
   * the rules allow. The man who slides later is named in `moves` like anybody else.
   */
  const placed = fit.unplaced.length === 0 ? fit.placed
    : (() => {
      const loose = fitKeepers(keepers, after, pinned);
      return loose.unplaced.length === 0 ? loose.placed : fitKeepers(keepers, after).placed;
    })();

  const moves: KeeperMove[] = [];
  for (const [player, to] of placed) {
    const from = now.get(player);
    /* A keeper who came in the trade has no round here yet: he is placed, and the card says so. */
    if (from === undefined) moves.push({ player, from: null, to });
    else if (from !== to) moves.push({ player, from, to });
  }
  moves.sort((x, y) => x.to - y.to || x.player.localeCompare(y.player));
  return { gm: who, shortfall: null, moves, placed };
}

/** "Waddle from Round 5 to Round 4, Bowers joins at Round 4" — one clause per move. */
export function movesSay(moves: readonly KeeperMove[], name: (p: string) => string): string {
  return moves.map((m) => (m.from === null
    ? `${name(m.player)} joins at Round ${m.to}`
    : `${name(m.player)} from Round ${m.from} to Round ${m.to}`)).join(', ');
}

/* --------------------------------------------------------------- keepers changing hands */

/**
 * A side's keepers once the players in the deal have moved.
 *
 * 2013-III-3 and 2018-III-2 between them: a trade does not reset a keeper's standing. He goes to
 * his new GM AS a keeper, at the round he already costs, and — Tim, 1 September — "the GM cannot
 * un-keep a player that has already been designated a keeper if the keeper deadline has passed".
 * So the sender's slate loses him and the receiver's gains him, and neither has a say in that
 * part; what the receiver decides is which of his picks pays, which is `keeperEffect`'s job.
 */
export function keepersAfterTrade(
  mine: readonly SlotKeeper[],
  sentPlayers: readonly string[],
  received: readonly SlotKeeper[],
): SlotKeeper[] {
  const gone = new Set(sentPlayers);
  const kept = mine.filter((k) => !gone.has(k.player));
  const names = new Set(kept.map((k) => k.player));
  return [...kept, ...received.filter((k) => !names.has(k.player))];
}

/**
 * Which of the players a side SENDS are its declared keepers, priced — the list the other side
 * receives as keepers.
 */
export function keepersSent(
  sends: ReadonlyArray<{ kind: string; text: string }>,
  keepers: readonly SlotKeeper[],
): SlotKeeper[] {
  const names = new Set(sends.filter((a) => a.kind === 'player').map((a) => a.text));
  return keepers.filter((k) => names.has(k.player));
}


/* ------------------------------------------------------------------ a GM's slate, priced */

/** The fields of a `keepers-2026.json` row this module reads. */
export interface PricedRow {
  player: string;
  team: string;
  cost: number | null;
  tagCost: number | null;
  farm?: boolean;
}

/**
 * A submitted slate as the resolver sees it: each keeper and the round he costs.
 *
 * Priced the way the declaration screen prices him — the Grid, or his own round under the
 * franchise tag (2025 P05 §2) — and a forfeited farm graduate left out, because he occupies no
 * pick (see `keeperContexts` on the draft board for the reading). Submitted slates only: a draft
 * is a manager still thinking, and a trade warning about a keeper he has not declared would be
 * the app arguing with him about a decision he has not made.
 */
export function pricedSlate(
  gm: string,
  decl: KeeperDeclaration | undefined,
  rows: readonly PricedRow[],
  gmOfTeam: (team: string) => string | undefined,
): SlotKeeper[] {
  if (!decl || decl.status !== 'submitted') return [];
  const who = gm.toUpperCase();
  const out: SlotKeeper[] = [];
  for (const k of decl.keepers) {
    if (k.farmForfeited) continue;
    const row = rows.find(
      (r) => r.player === k.player && (gmOfTeam(r.team) ?? r.team).toUpperCase() === who,
    );
    const cost = k.franchiseTagged ? row?.tagCost ?? null : row?.cost ?? null;
    if (cost === null) continue;
    out.push({ player: k.player, costRound: cost });
  }
  return out;
}

/**
 * Where each keeper is charged TODAY — the GM's own `elevatedTo` honoured, collisions walked
 * back the way every other screen walks them. This is the `now` that `keeperEffect` measures
 * moves from and caps them at.
 */
export function chargedNow(
  keepers: readonly SlotKeeper[],
  decl: KeeperDeclaration | undefined,
  ownedRounds: readonly number[],
): Map<string, number> {
  const pinned = new Map(decl?.keepers.map((k) => [k.player, k.elevatedTo ?? null]) ?? []);
  const { assigned } = assignKeeperPicks(
    keepers.map((k) => ({ id: k.player, costRound: k.costRound, preferred: pinned.get(k.player) ?? null })),
    ownedRounds,
  );
  return assigned;
}

/**
 * The keeper standing on each specific pick, keyed `round:SEAT`.
 *
 * Within a round the specific pick comes from `chargedPick.from` where the declaration recorded
 * one and the GM's own seat otherwise — the same default the trade form uses for a pick with no
 * origin, so the two cannot name different cells.
 */
export function keepersOnPicks(
  gm: string,
  decl: KeeperDeclaration | undefined,
  charged: ReadonlyMap<string, number>,
): Map<string, string> {
  const who = gm.toUpperCase();
  const out = new Map<string, string>();
  const taken = new Set<string>();
  for (const [player, round] of charged) {
    const rec = decl?.keepers.find((k) => k.player === player)?.chargedPick;
    const seat = (rec && rec.round === round ? rec.from : who).toUpperCase();
    let key = `${round}:${seat}`;
    /* Two keepers in one round on a GM with one pick there cannot both stand on it; the
       resolver already prevented that, so this only ever disambiguates two picks in a round. */
    if (taken.has(key)) key = `${round}:${who}`;
    taken.add(key);
    out.set(key, player);
  }
  return out;
}

/* ------------------------------------------------------------------------ the farm cap */

/**
 * Whether the receiver has room for the farm rights coming his way (2022 §1, three).
 *
 * Tim: farm rights are tradeable "if other GM has space". The sender's count falls by what he
 * sends, the receiver's rises by what he takes, and a trade that would put him past the cap has
 * no roster to land on and is refused rather than warned about.
 */
export function farmCapCheck(
  gm: string,
  holding: number,
  incoming: number,
  outgoing: number,
  max: number,
): { ok: boolean; after: number; why: string } {
  const after = holding - outgoing + incoming;
  if (incoming === 0 || after <= max) return { ok: true, after, why: '' };
  return {
    ok: false,
    after,
    why: `${gm} would hold ${after} farm players; the cap is ${max} (2022 §1). `
      + `He has to send ${after - max} back, or make room first.`,
  };
}
